/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { LuiSelect } from '@jda/lui-common-component-library-mui5';
import { SignPlus } from '@jda/lui-common-icon-library-mui5';
import { Box, Button, Grid, Typography } from '@mui/material';
import LocationTable from '../../components/LocationTable/LocationTable';
import AddLocationDialog from '../../components/AddLocationDialog/AddLocationDialog';
import CustomSearch from '../../components/CustomSearch';
import { AlertDialog } from '../../shared/Dialog/AlertDialog';
import { useStyles } from './styles';
import {
  EMPTY_SCREEN_TEXT,
  SEARCH,
  FOR,
  ADD_LOCATION,
  DELETE_LOCATION_CONTENT,
  CANCEL,
  ERROR_MESSAGE_GENERAL,
  PASSWORD_POLICY
} from './constants';
import { LABELS, DELETE_LOCATION } from '../../components/LocationTable/constants';
import EmptyComponent from '../../shared/EmptyComponent/EmptyComponent';

import { fetchLocations } from "../../store/locations/locations";
import {
  selectLocations,
  selectLocationsError,
  selectLocationsLoading,
  selectPaginationDetails
} from "../../store/locations/locationsSelector";
import PasswordPolicyRuleDetail from "../../components/PasswordPolicies/PasswordPolicyRuleDetail/PasswordPolicyRuleDetail";
import Loader from "../../shared/Loader";
import DetailsModal from "../../shared/Dialog/DetailsModal";
import SnackbarAlert from '../../shared/SnackbarAlert';
import { deleteLocation } from "../../services/locations";
import { setSnackBarMessage } from "../../store/snackBar/snackBarActionCreator";
import { SNACKBAR_ALERT_DEFAULT_PROPS } from "../../components/RoleDetails/constants";
import { ErrorMessage } from "../../shared/ErrorMessage/ErrorMessage";
import { getAllPasswordPolicy, getPasswordPolicyRule } from "../../services/setting";
import { checkResourcePermission } from "../../shared/CheckPermissions/CheckPermission";
import { actions as resourceActions, resources } from "../../shared/CheckPermissions/constants";
import { selectResourcePermissions } from "../../store/auth/authSelector";


const searchOptions = [
  { key: 'locationType', displayName: LABELS.LOCATION_TYPE },
  { key: 'locationName', displayName: LABELS.LOCATION_NAME }
];

const Locations = () => {
  const [deletingLocationId, setDeletingLocationId] = useState<string>();
  const [searchQuery, setSearchQuery] = useState<any>({ searchQuery: '', trigger: false });
  const [locationType, setLocationType] = useState<any>(searchOptions[0]);
  const [locationDialogState, setLocationDialogState] = useState<
    { status: 'create' } | { status: 'edit'; locationId: string }
  >();
  const [locationPasswordPolicyDialogState, setLocationPasswordPolicyDialogState] = useState<boolean>(false);
  const [passwordPolicy, setPasswordPolicy] = useState<any>([]);
  const [showPasswordPolicyLoader, showHidePasswordPolicyLoader] = useState(false);
  const resourcePermissions = useSelector(selectResourcePermissions);
  const locations = useSelector(selectLocations);
  const loading = useSelector(selectLocationsLoading);
  const error = useSelector(selectLocationsError);
  const { pageNumber, pageSize } = useSelector(selectPaginationDetails);
  const dispatch = useDispatch();

  const { classes } = useStyles();

  const isCreateLocationPolicyAllowed =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(resourceActions.CREATE, resourcePermissions.resources[resources.LOCATIONS]);

  useEffect(() => {
    const pageDetails = {
      pageNumber, pageSize
    }
    fetchLocations(dispatch, pageDetails);
  }, [dispatch, pageNumber, pageSize]);

  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        message: message,
        severity: severity
      })
    );
  };

  const openLocationCreateDialog = () => {
    setLocationDialogState({ status: 'create' });
  };

  const openLocationEditDialog = (id: string) => {
    setLocationDialogState({ status: 'edit', locationId: id });
  };

  const openLocationPasswordPolicyDialog = async (
    passwordPolicyId: string,
    locationName: string
  ) => {
    showHidePasswordPolicyLoader(true);

    try {
      let response = {};
      if (passwordPolicyId !== null) {
        response = await getPasswordPolicyRule(passwordPolicyId, locationName);
      } else {
        const policies = await getAllPasswordPolicy();
        const defaultPolicy = policies.find((item: any) => item.isDefault);
        if (!defaultPolicy) {
          throw new Error(ERROR_MESSAGE_GENERAL);
        }
        response = await getPasswordPolicyRule(defaultPolicy.id);
      }

      setPasswordPolicy(response);
      setLocationPasswordPolicyDialogState(true);
    } catch (err: any) {
      displaySnackbar(err?.message || ERROR_MESSAGE_GENERAL, 'error');
    } finally {
      showHidePasswordPolicyLoader(false);
    }
  };

  const handleDeleteLocation = () => {
    deleteLocation(deletingLocationId)
      .then(() => {
        setDeletingLocationId(undefined);
        displaySnackbar('Location was successfully deleted', 'success');
        fetchLocations(dispatch, {});
      })
      .catch((error: any) => displaySnackbar(error.response?.data?.message || error.message, 'error'))
  };
  const closeLocationDialog = () => {
    setLocationDialogState(undefined);
  };

  const deletingLocation = useMemo(() => locations.find((location: any) => location.id === deletingLocationId), [
    deletingLocationId,
    locations
  ]);

  const handleSearchChange = (searchQuery: any) => {
    setSearchQuery({ ...searchQuery, searchQuery: searchQuery, trigger: true })
    const pageDetails = {
      pageNumber,
      pageSize,
      [locationType.key]: searchQuery,
    };
    if (searchQuery !== "") {
      fetchLocations(dispatch, pageDetails);
    } else {
      setSearchQuery({ ...searchQuery, searchQuery: '', trigger: true })
      fetchLocations(dispatch, { pageNumber, pageSize });
    }
  };

  const handleDropdownChange = (e: any) => {
    setLocationType(e.target.value);
    const pageDetails = {
      pageNumber,
      pageSize,
      [e.target.value.key]: searchQuery.searchQuery
    };
    if (searchQuery.searchQuery !== '') {
      fetchLocations(dispatch, pageDetails);
    }
  };

  const handleLocationDialogClose = () => {
    setLocationPasswordPolicyDialogState(false);
  };

  return (
    <Box className={classes.root}>
      {locations.length === 0 &&
        !loading &&
        searchQuery.searchQuery === '' &&
        !error &&
        searchQuery.trigger === false ? (
        <EmptyComponent
          text={EMPTY_SCREEN_TEXT}
          title=""
          primaryButtonText={ADD_LOCATION}
          disableCreateAction={!isCreateLocationPolicyAllowed}
          handleOnCreateAction={openLocationCreateDialog}
        />
      ) : (
        <>
          <Grid container className={classes.toolbar}>
            <Grid item xs={6} className={classes.searchContainer}>
              <Box className={classes.searchField}>
                <CustomSearch
                  value={searchQuery.searchQuery}
                  placeHolder={SEARCH}
                  onSearchChange={handleSearchChange}
                />
              </Box>
              <Box className={classes.conjuction}>
                <Typography>{FOR}</Typography>
              </Box>
              <Box className={classes.searchKeyDD}>
                <LuiSelect
                  selectedItem={locationType}
                  inputLabel=""
                  itemData={searchOptions}
                  defaultValue={searchOptions[0]}
                  width={160}
                  maxHeight={100}
                  onSelectionChange={(e: any) => handleDropdownChange(e)}
                />
              </Box>
            </Grid>
            {isCreateLocationPolicyAllowed && <Grid item xs={6} className={classes.buttonPanel}>
              <Button
                size="medium"
                color="primary"
                aria-label={ADD_LOCATION}
                onClick={openLocationCreateDialog}
                disableElevation
                variant="contained"
                startIcon={<SignPlus />}
              >
                {ADD_LOCATION}
              </Button>
            </Grid>}
          </Grid>
          {!error && (
            <LocationTable
              data={{ locations, loading }}
              onLocationDelete={setDeletingLocationId}
              onLocationEdit={openLocationEditDialog}
              onLocationsPasswordPolicyView={openLocationPasswordPolicyDialog}
            />
          )}
          {error && <ErrorMessage message={error.response?.data?.message || error.message} />}
        </>
      )}

      {locationDialogState && (
        <AddLocationDialog
          onSave={() => {
            fetchLocations(dispatch, {});
          }}
          onClose={closeLocationDialog}
          locationId={locationDialogState?.status === 'edit' ? locationDialogState.locationId : null}
        />
      )}
      {deletingLocation && (
        <AlertDialog
          dialogTitle={DELETE_LOCATION_CONTENT}
          dialogContentText={DELETE_LOCATION}
          recordName={deletingLocation.name}
          primaryButtonText={DELETE_LOCATION}
          secondButtonText={CANCEL}
          handleOnCancel={() => {
            setDeletingLocationId(undefined);
          }}
          handleOnOk={handleDeleteLocation}
          showDialogLoader={false}
        />
      )}
      {showPasswordPolicyLoader && <Loader showLoader={true} isCenter customClasses={classes.centerLoader} />}
      {locationPasswordPolicyDialogState && (
        <DetailsModal
          openModal={locationPasswordPolicyDialogState}
          handleClose={handleLocationDialogClose}
          dialogTitle={PASSWORD_POLICY}
          maxWidth={'md'}
          data-testid="location-modal"
        >
          <PasswordPolicyRuleDetail policyDetails={passwordPolicy} />
        </DetailsModal>
      )}
      <SnackbarAlert />
    </Box>
  );
};

const LocationsDescription = {
  component: Locations,
  name: 'Sub-Realms',
};

export default LocationsDescription;
